/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-14 10:46:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import Upload from 'src/components/views/synonym/import/upload';
import ImportDetails from 'src/components/views/synonym/import/details';
import { routes } from 'src/routes';
import history from 'src/routes/history';

import * as synonymActions from 'src/stores/actions/synonym-actions';

const initialState = {
  status: 'upload',
  selectedFiles: [],
  fileContentErrorCode: '',
  selectedFileName: '',
  selectedFileSize: 0,
  totalImportedRecords: 0
};

type Props = {
  active: boolean,
  verifying: boolean,
  importing: boolean,
  title: string,
  content: string,
  handleChange: () => void,
  verifyFileContent: () => void,
  handleImport: () => void,
  handleViewAllSynonym: () => void,
  getSynonymsFirstPage: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selectedFiles: Array<Object>,
  fileContentErrorCode: string,
  status: string,
  selectedFileName: string,
  selectedFileSize: number,
  totalImportedRecords: number
};

class ImportModal extends Component<ComposedProps, State> {
  state = {
    selectedFiles: [],

    // Show verify content error
    fileContentErrorCode: '',

    // Modal stage
    status: 'upload',

    // Verify file content
    selectedFileName: '',
    selectedFileSize: 0,

    // Import file content
    totalImportedRecords: 0
  };

  render() {
    // Status = 'upload' || 'details' || 'complete'
    const { i18n, active, totalRecord, totalImportedRecords, verifying, importing } = this.props;
    const { status, fileContentErrorCode, selectedFileName, selectedFileSize, selectedFiles } =
      this.state;

    // Primary action based on the step of importing
    const primaryAction = {
      upload: {
        content: i18n.translate('AdminHTML.Common.Action.upload'),
        loading: verifying,
        disabled: _.isEmpty(selectedFiles) || !_.isEmpty(fileContentErrorCode),
        onAction: this.handleUpload
      },
      details: {
        content: i18n.translate('AdminHTML.Common.Action.import'),
        loading: importing,
        onAction: this.handleImport
      },
      complete: {
        content: i18n.translate('AdminHTML.Synonym.Import.viewAll'),
        onAction: this.handleViewAllSynonyms
      }
    };

    const secondaryActions = {
      upload: [
        {
          content: i18n.translate('AdminHTML.Common.Action.cancel'),
          onAction: this.handleChange
        }
      ],
      details: [
        {
          content: i18n.translate('AdminHTML.Common.Action.cancel'),
          onAction: this.handleChange
        }
      ],
      complete: [
        {
          content: i18n.translate('AdminHTML.Synonym.Import.importMore'),
          onAction: this.handleImportMoreSynonyms
        }
      ]
    };

    const content = {
      upload: (
        <Upload onSelectFile={this.onSelectFile} fileContentErrorCode={fileContentErrorCode} />
      ),
      details: (
        <ImportDetails
          status="details"
          fileName={selectedFileName}
          fileSize={selectedFileSize}
          total={totalRecord}
        />
      ),
      complete: (
        <ImportDetails
          status="complete"
          fileName={selectedFileName}
          fileSize={selectedFileSize}
          total={totalImportedRecords}
        />
      )
    };

    const title = {
      upload: i18n.translate('AdminHTML.Synonym.Import.upload'),
      details: i18n.translate('AdminHTML.Synonym.Import.details'),
      complete: i18n.translate('AdminHTML.Synonym.Import.complete')
    };

    return (
      <Modal
        large
        open={active}
        onClose={this.handleChange}
        title={title[status]}
        primaryAction={primaryAction[status]}
        secondaryActions={secondaryActions[status]}>
        <Modal.Section>{content[status]}</Modal.Section>
      </Modal>
    );
  }

  // Handle hide/show modal
  handleChange = () => {
    // Reset to upload screen
    let updateState = { status: 'upload', selectedFiles: [] };

    // Hide error banner
    const { fileContentErrorCode } = this.state;
    if (!_.isEmpty(fileContentErrorCode)) {
      updateState = _.assign(updateState, { fileContentErrorCode: '' });
    }

    this.setState(updateState);

    // Hide/show modal
    this.props.handleChange();
  };

  // Handle verify file content
  handleUpload = async () => {
    const { selectedFiles } = this.state;
    await this.props.verifyFileContent(selectedFiles);

    // Show error block
    if (!_.isEmpty(this.props.fileContentErrorCode)) {
      this.setState({ fileContentErrorCode: this.props.fileContentErrorCode });
    } else {
      // Get file size
      const { selectedFiles } = this.state;
      const { name, size } = selectedFiles[0];

      // Hide error block and go to file details screen
      this.setState({
        fileContentErrorCode: '',
        status: 'details',
        selectedFileName: name,
        selectedFileSize: size
      });
    }
  };

  // Handle on select file from dropzone
  onSelectFile = selectedFiles => {
    this.setState({ selectedFiles });
  };

  // Handle import file content
  handleImport = async () => {
    const { selectedFiles } = this.state;
    await this.props.importFile(selectedFiles);

    // Go to complete screen
    const { success, totalImportedRecords } = this.props;
    if (success) {
      this.setState({ totalImportedRecords, status: 'complete' });
      this.props.getSynonymsFirstPage();
    }
  };

  // Go to management page
  handleViewAllSynonyms = () => {
    const { handleChange } = this.props;

    // Hide modal
    handleChange();

    this.setState(initialState);

    // Back to management synonym
    history.push(routes.synonym.view());
  };

  // Back to upload screen
  handleImportMoreSynonyms = () => {
    this.setState(initialState);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  verifyFileContent: files => dispatch(synonymActions.verifyFileContent(files)),
  importFile: files => dispatch(synonymActions.importFile(files, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    // Verify
    verifying: _.get(state.synonym, 'verifying', false),
    fileContentErrorCode: _.get(state.synonym, 'fileContentErrorCode', ''),
    totalRecord: _.get(state.synonym, 'totalRecord', 0),

    // Import
    importing: _.get(state.synonym, 'importing', false),
    totalImportedRecords: _.get(state.synonym, 'totalImportedRecords', 0),
    success: _.get(state.synonym, 'success', 0)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ImportModal));
